import { gqlTypes } from '@/types/gqlTypes';
import { PureQueryOptions, useMutation } from '@apollo/client';
import {
  reassignChipToResellerMutation,
  resellerChipDetailsQuery,
} from '../ResellerChipDetails/ResellerChipDetails.graphql';
import { viewingChipsQuery } from './ViewingChips.graphql';
import ModalAlert from '../components/modals/ModalAlert';
import ModalActions from '../components/modals/ModalActions/ModalActions';
import { useRef, useState } from 'react';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import styles from './ReassignFiChipModal.module.scss';

export interface ReassignFiChipModalProps {
  serialNumber: string;
  /** Whether the chip has owner info (latest contact history entry) */
  hasOwnerInfo: boolean;
  onClose: (success?: boolean) => void;
  onSuccess?: (clearOwnerInfo: boolean) => void;
  refetchQueries?: PureQueryOptions[];
}

export function ReassignFiChipModal({
  serialNumber,
  hasOwnerInfo,
  onClose,
  onSuccess,
  refetchQueries = [],
}: ReassignFiChipModalProps) {
  const [clearOwnerInfo, setClearOwnerInfo] = useState(false);

  const PAGE_SIZE = 20;
  const OFFSET = 0;

  const [reassignChip, { error, loading, called }] = useMutation<
    gqlTypes.reassignChipToReseller,
    gqlTypes.reassignChipToResellerVariables
  >(reassignChipToResellerMutation, {
    refetchQueries: [
      { query: resellerChipDetailsQuery, variables: { id: serialNumber } },
      {
        query: viewingChipsQuery,
        variables: { limit: PAGE_SIZE, offset: OFFSET, statusType: gqlTypes.ChipStatusType.SHIPPED },
      },
      {
        query: viewingChipsQuery,
        variables: { limit: PAGE_SIZE, offset: OFFSET, statusType: gqlTypes.ChipStatusType.ACTIVE },
      },
      ...refetchQueries,
    ],
    awaitRefetchQueries: true,
  });

  if (error) {
    return (
      <ModalAlert message="There was an error reassigning this chip, please try again." open={true} onClose={onClose} />
    );
  }

  const chipDetailsUrl = `/verifysearch?q=${encodeURIComponent(serialNumber)}`;

  const ref = useRef<PopupActions>(null);
  return (
    <Popup open={true} modal contentStyle={{ padding: '0', fontSize: '26px' }} ref={ref} onClose={() => onClose()}>
      <div style={{ padding: '2em', fontSize: '0.8625em' }}>
        <p className={styles.header}>
          This Fi chip isn't linked to your organization. Do you want to assign it to your organization now?
        </p>

        {hasOwnerInfo && (
          <>
            <p className={styles.bodyText}>
              Assigning this chip will link it to your organization. You can choose whether to clear or keep the
              existing owner information.
            </p>

            <div className={styles.viewOwnerLinkContainer}>
              <a href={chipDetailsUrl} target="_blank" rel="noopener noreferrer" className={styles.viewOwnerLink}>
                View current owner information
              </a>
              <span className={styles.newTabHint}>(Opens in a new tab)</span>
            </div>

            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="ownerInfo"
                  checked={!clearOwnerInfo}
                  onChange={() => setClearOwnerInfo(false)}
                  className={styles.radioInput}
                />
                <div className={styles.radioContent}>
                  <span className={styles.radioTitle}>Keep existing owner info</span>
                  <span className={styles.radioDescription}>
                    Owner details will stay on the chip, but your rescue will be added as the current organization.
                  </span>
                </div>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="ownerInfo"
                  checked={clearOwnerInfo}
                  onChange={() => setClearOwnerInfo(true)}
                  className={styles.radioInput}
                />
                <div className={styles.radioContent}>
                  <span className={styles.radioTitle}>Clear owner info</span>
                  <span className={styles.radioDescription}>
                    All owner details will be removed, and the chip will be assigned to your organization.
                  </span>
                </div>
              </label>
            </div>
          </>
        )}
      </div>
      <form>
        <ModalActions
          actions={[
            {
              text: 'Assign to My Organization',
              primary: true,
              onClick: async () => {
                if (!loading && !called) {
                  const result = await reassignChip({
                    variables: { input: { serialNumber, clearOwnerInfo } },
                  });
                  if (result) {
                    onSuccess?.(clearOwnerInfo);
                  }
                  onClose(!!result);
                }
              },
            },
            {
              text: 'Cancel',
              onClick: () => onClose(false),
            },
          ]}
        />
      </form>
    </Popup>
  );
}
